import './style.css'
import { loadRegions, loadSeed } from './data.js'
import { getStore, setData, setRegions, setRegion, setPlayer, setGameState, subscribe, isFeatureAvailable } from './store.js'
import { getState } from './api.js'
import { icon } from './icons.js'
import { renderMap, mountMap, unmountMap } from './views/map.js'
import { renderNode } from './views/node.js'
import { renderPeople, renderPerson, renderNearestCeleb } from './views/people.js'
import { renderEncyclopedia, renderEncyclopediaItem } from './views/encyclopedia.js'
import { renderBingo } from './views/bingo.js'
import { renderQuiz } from './views/quiz.js'
import { renderPhrases } from './views/phrases.js'
import { renderHighlights, renderHighlight } from './views/highlights.js'
import { renderHikes, renderHike } from './views/hikes.js'
import { renderHome } from './views/home.js'
import { renderNearby } from './views/nearby.js'
import { renderRegionSelect } from './views/region-select.js'

const $view = document.getElementById('view')
const $loader = document.getElementById('loader')
const $tabbar = document.getElementById('tabbar')
const $topbar = document.getElementById('topbar')
const $topbarTitle = document.getElementById('topbar-title')
const $backBtn = document.getElementById('back-btn')
const $homeLink = document.getElementById('topbar-home-link')

// ---------- Tab-configuratie (data-gedreven) ----------
const TAB_DEFS = [
  { id: 'home',         label: 'Home',       href: 'home',         icon: 'home',       always: true },
  { id: 'highlights',   label: 'Highlights', href: 'highlights',   icon: 'compass',    feature: 'guides',     collection: 'guides' },
  { id: 'map',          label: 'Kaart',      href: 'map',          icon: 'map',        always: true },
  { id: 'hikes',        label: 'Hikes',      href: 'hikes',        icon: 'footprints', feature: 'hikes',      collection: 'hikes' },
  { id: 'bingo',        label: 'Bingo',      href: 'bingo',        icon: 'grid',       feature: 'bingo',      collection: 'bingo' },
  { id: 'encyclopedie', label: 'Info',       href: 'encyclopedie', icon: 'book-open',  feature: 'compendium', collection: 'compendium' },
]

function getVisibleTabs() {
  const data = getStore().data
  if (!data) return TAB_DEFS.filter(t => t.always)
  return TAB_DEFS.filter(t =>
    t.always || isFeatureAvailable(t.feature, t.collection)
  )
}

// Feature-to-route mapping: welke routes zijn geblokkeerd als hun feature uit staat?
const ROUTE_FEATURE = {
  bingo:            { feature: 'bingo',     collection: 'bingo' },
  quiz:             { feature: 'quiz',      collection: 'quiz' },
  people:           { feature: 'people',    collection: 'people' },
  person:           { feature: 'people',    collection: 'people' },
  'nearest-celeb':  { feature: 'people',    collection: 'people' },
  phrases:          { feature: 'phrases',   collection: 'phrases' },
  highlights:       { feature: 'guides',    collection: 'guides' },
  highlight:        { feature: 'guides',    collection: 'guides' },
  hikes:            { feature: 'hikes',     collection: 'hikes' },
  hikeDetail:       { feature: 'hikes',     collection: 'hikes' },
  encyclopedie:     { feature: 'compendium', collection: 'compendium' },
  encyclopediaItem: { feature: 'compendium', collection: 'compendium' },
}

// ---------- Routing ----------
const ALIASES = {
  tips: 'highlights',
  compendium: 'encyclopedie',
  game: 'bingo',
}

function headToTab(head) {
  const map = { home: 'home', map: 'map', node: 'map' }
  if (map[head]) return map[head]
  const tab = TAB_DEFS.find(t => t.href === head)
  return tab ? tab.id : null
}

function parseHash() {
  const [hashPath, qs] = (location.hash || '').replace(/^#\/?/, '').split('?')
  const parts = hashPath.split('/').filter(Boolean)
  const query = Object.fromEntries(new URLSearchParams(qs || ''))

  if (!parts.length) return { region: null, tab: null, kind: 'regionSelect', params: {} }

  const store = getStore()
  const regionIds = (store.regions ?? []).map(r => r.id)

  if (regionIds.includes(parts[0])) {
    const region = parts[0]
    let [head, ...rest] = parts.slice(1)
    head = ALIASES[head] || head || 'home'
    const tab = headToTab(head)

    if (head === 'node' && rest[0])         return { region, tab, kind: 'node',              params: { id: rest[0], ...query } }
    if (head === 'people' && rest[0])       return { region, tab, kind: 'person',            params: { id: rest[0], ...query } }
    if (head === 'encyclopedie' && rest[0]) return { region, tab, kind: 'encyclopediaItem',  params: { id: rest[0], ...query } }
    if (head === 'highlights' && rest[0])   return { region, tab, kind: 'highlight',         params: { id: rest[0], ...query } }
    if (head === 'hikes' && rest[0])        return { region, tab, kind: 'hikeDetail',        params: { id: rest[0], ...query } }
    return { region, tab, kind: head, params: { ...query } }
  }

  return { region: null, tab: null, kind: 'regionSelect', params: {} }
}

export function navigate(hash) {
  if (location.hash === hash) {
    onRouteChange()
  } else {
    location.hash = hash
  }
}

export function regionHash(path) {
  const store = getStore()
  const rid = store.region?.id
  if (!rid) return '#/'
  return `#/${rid}/${path.replace(/^\//, '')}`
}

// ---------- Click-interceptor: prefix alle #/... links met de regio ----------
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href^="#/"]')
  if (!a) return
  const href = a.getAttribute('href')
  const store = getStore()
  const rid = store.region?.id
  if (!rid) return

  const prefix = `#/${rid}/`
  if (href.startsWith(prefix)) return
  if (href === '#/' || href === '#') return

  const regionIds = (store.regions ?? []).map(r => r.id)
  const firstSeg = href.replace(/^#\/?/, '').split('/')[0]
  if (regionIds.includes(firstSeg)) return

  e.preventDefault()
  location.hash = prefix + href.replace(/^#\/?/, '')
})

// ---------- Tabbar ----------
function buildTabbar() {
  const tabs = getVisibleTabs()
  const rid = getStore().region?.id ?? ''
  $tabbar.innerHTML = tabs.map(t => `
    <a href="#/${rid}/${t.href}" data-tab="${t.id}" class="tab${t.id === 'home' ? ' tab-home' : ''}">
      <span class="tab-icon" aria-hidden="true">${icon(t.icon)}</span>
      <span class="tab-label">${t.label}</span>
      ${t.id !== 'home' ? '<span class="tab-indicator" aria-hidden="true"></span>' : ''}
    </a>
  `).join('')
}

function paintTabbar(route) {
  const hideTabbar = route.kind === 'home' || route.kind === 'regionSelect'
  $tabbar.hidden = hideTabbar
  document.querySelectorAll('#tabbar .tab').forEach(a => {
    a.classList.toggle('active', route.tab != null && a.dataset.tab === route.tab)
  })
}

function paintTopbar(route) {
  const detailRoutes = ['node', 'person', 'encyclopediaItem', 'highlight', 'hikeDetail', 'quiz', 'phrases', 'nearest-celeb', 'people']
  const isDetail = detailRoutes.includes(route.kind)
  $topbar.hidden = !isDetail && route.kind !== 'regionSelect'
  $backBtn.hidden = !isDetail

  if (route.kind === 'regionSelect') {
    $topbar.hidden = true
  }

  if ($homeLink) $homeLink.textContent = ''
}

// ---------- Region-switch knop in de topbar ----------
const $regionBtn = document.createElement('button')
$regionBtn.className = 'icon-btn region-switch-btn'
$regionBtn.setAttribute('aria-label', 'Wissel van regio')
$regionBtn.addEventListener('click', () => {
  location.hash = '#/'
})

function updateRegionButton() {
  const store = getStore()
  if (store.region && store.route?.kind !== 'regionSelect') {
    $regionBtn.innerHTML = `${icon('map-pin')} <span class="region-switch-label">${store.region.label}</span>`
    if (!$regionBtn.parentNode) {
      const spacer = document.getElementById('topbar-spacer')
      if (spacer) spacer.replaceWith($regionBtn)
    }
  }
}

// ---------- View-renderen ----------
function renderRoute(route) {
  const store = getStore()

  if (route.kind === 'regionSelect') {
    unmountMap()
    $view.innerHTML = ''
    delete document.documentElement.dataset.region
    renderRegionSelect($view, store.regions ?? [])
    return
  }

  // Check of route beschikbaar is voor deze regio
  const rf = ROUTE_FEATURE[route.kind]
  if (rf && !isFeatureAvailable(rf.feature, rf.collection)) {
    location.hash = regionHash('home')
    return
  }

  const ctx = {
    store,
    data: store.data,
    indexes: store.indexes,
    navigate,
    route,
    region: store.region,
  }

  if (route.kind === 'home') {
    unmountMap()
    $view.innerHTML = ''
    renderHome($view, ctx)
    return
  }

  if (route.kind === 'map') {
    $view.innerHTML = ''
    renderMap($view, ctx)
    return
  }

  unmountMap()
  $view.innerHTML = ''

  switch (route.kind) {
    case 'node':              renderNode($view, ctx, route.params.id); break
    case 'people':            renderPeople($view, ctx); break
    case 'person':            renderPerson($view, ctx, route.params.id); break
    case 'encyclopedie':      renderEncyclopedia($view, ctx); break
    case 'encyclopediaItem':  renderEncyclopediaItem($view, ctx, route.params.id); break
    case 'bingo':             renderBingo($view, ctx); break
    case 'quiz':              renderQuiz($view, ctx); break
    case 'phrases':           renderPhrases($view, ctx); break
    case 'highlights':        renderHighlights($view, ctx); break
    case 'highlight':         renderHighlight($view, ctx, route.params.id); break
    case 'hikes':             renderHikes($view, ctx); break
    case 'hikeDetail':        renderHike($view, ctx, route.params.id); break
    case 'nearest-celeb':     renderNearestCeleb($view, ctx); break
    case 'nearby':            renderNearby($view, ctx); break
    default:                  renderHome($view, ctx)
  }
}

let currentRegionId = null

async function onRouteChange() {
  const route = parseHash()
  const store = getStore()
  store.route = route

  if (route.kind === 'regionSelect') {
    currentRegionId = null
    setRegion(null)
    paintTabbar(route)
    paintTopbar(route)
    renderRoute(route)
    $tabbar.hidden = true
    return
  }

  if (route.region && route.region !== currentRegionId) {
    const region = (store.regions ?? []).find(r => r.id === route.region)
    if (!region) {
      location.hash = '#/'
      return
    }

    $loader.classList.remove('hide')
    $loader.querySelector('.loader-text').textContent = `${region.label} wordt geladen…`

    try {
      const { data, indexes } = await loadSeed(region.file)
      setRegion(region)
      setData(data, indexes)
      currentRegionId = region.id
      document.title = 'Reisgids'
      document.documentElement.dataset.region = region.id
      buildTabbar()
      updateRegionButton()
    } catch (e) {
      $loader.classList.add('error')
      $loader.querySelector('.loader-text').textContent = `Fout bij laden: ${e.message}`
      return
    }

    $loader.classList.add('hide')

    getState()
      .then(s => {
        setPlayer({ id: s.playerId, name: s.name ?? null })
        setGameState({
          bingo: s.bingo, quizDone: s.quizDone, scores: s.scores,
          favorites: s.favorites, discoveries: s.discoveries || [],
        })
      })
      .catch(err => console.warn('/api/state niet bereikbaar (mag in dev):', err.message))
  }

  paintTabbar(route)
  paintTopbar(route)
  updateRegionButton()
  renderRoute(route)
  window.scrollTo(0, 0)
}

// ---------- Offline banner ----------
let offlineBanner = null

function showOfflineBanner() {
  if (offlineBanner) return
  offlineBanner = document.createElement('div')
  offlineBanner.className = 'offline-banner'
  offlineBanner.innerHTML = `${icon('wifi-off')} <span>Geen internetverbinding — sommige functies werken mogelijk niet.</span>`
  document.body.prepend(offlineBanner)
}

function hideOfflineBanner() {
  if (!offlineBanner) return
  offlineBanner.remove()
  offlineBanner = null
}

// ---------- Boot ----------
async function boot() {
  $backBtn.innerHTML = icon('arrow-left')
  $backBtn.addEventListener('click', () => history.back())

  window.addEventListener('hashchange', onRouteChange)
  window.addEventListener('offline', showOfflineBanner)
  window.addEventListener('online', hideOfflineBanner)
  if (!navigator.onLine) showOfflineBanner()

  try {
    const regions = await loadRegions()
    setRegions(regions)
  } catch (e) {
    $loader.classList.add('error')
    $loader.querySelector('.loader-text').textContent = `Fout bij laden: ${e.message}`
    return
  }

  if (!location.hash || location.hash === '#' || location.hash === '#/') {
    const store = getStore()
    if (store.regions?.length === 1) {
      location.hash = `#/${store.regions[0].id}/home`
    } else {
      location.hash = '#/'
    }
  }

  $loader.classList.add('hide')
  onRouteChange()
}

export function setTitle(t) {
  if ($homeLink) $homeLink.textContent = t ?? ''
  else $topbarTitle.textContent = t ?? ''
}

subscribe((reason) => {
  if (reason === 'generated' && getStore().route.kind === 'node') {
    renderRoute(getStore().route)
  }
})

boot()
